.navbar-type-1 {
  position: relative;
  width: 100%;
  z-index: 100;
  background-color: var(--background-color-primary);
  color: var(--text-color-primary);

  // Note: .navbar-type-1--sticky class is still set in render() but has no styles.
  // Sticky positioning moved to the HOST element (navbar-type-1 custom element)
  // via .is-sticky-host class. See componentDidLoad() and the bottom of this file.

  &--with-border {
    border-bottom: 1px solid var(--border-color-primary);
  }

  // Mobile menu is full-width by default now.
  // Keep modifier for backward compatibility (no-op).
  &--mobile-full-width {
    .navbar-type-1__container--mobile {
      width: 100%;
    }
  }

  &.is-fixed {
    box-shadow: 0 4px 12px rgb(44 44 44 / 12%);
  }

  &__container {
    display: flex;

    @include container;

    /* ✓ Figma mobile: pl-20px pr-12px */
    padding: calc(var(--spacing-md) - 2px) var(--spacing-sm) calc(var(--spacing-md) - 2px) var(--spacing-lg); /* ✓ Figma: 12px */ /* ✓ Figma: 20px */

    @include above($breakpoint-desktop-md) {
      padding: 0 var(--spacing-5xl) 0 var(--spacing-5xl); /* ✓ Figma desktop: 64px */ /* ✓ Figma desktop: 64px */
    }

    &--desktop {
      z-index: 100;
      align-items: center;
      justify-content: space-between;
      height: $navbar-height-mobile; /* ✓ Figma mobile: 64px */

      @include above($breakpoint-desktop-md) {
        height: $navbar-height-desktop; /* ✓ Figma desktop: 72px */
      }

      .me-button {
        white-space: nowrap;
      }
    }

    &--mobile {
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%; /* ✓ Figma: full-width menu panel */
      background-color: var(--background-color-primary);
      z-index: 99;
      overflow-y: auto;
      transform: translateX(-100%);
      visibility: hidden;
      opacity: 0;
      transition:
        transform 0.3s ease-in-out,
        opacity 0.25s ease-in-out,
        visibility 0s linear 0.3s;
      padding: 0;
      align-items: flex-start;
      touch-action: pan-y;
      border-top: 1px solid var(--border-color-primary); /* ✓ border between bar and menu */

      @include container;

      &.is-open {
        transform: translateX(0);
        visibility: visible;
        opacity: 1;
        transition:
          transform 0.3s ease-in-out,
          opacity 0.25s ease-in-out,
          visibility 0s linear 0s;
      }

      .navbar-type-1__mobile-content {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xl); /* 24px between nav group and actions */
        padding-top: var(--spacing-lg); /* ✓ Figma: 20px top padding for menu content */
      }

      // Make slotted nav wrapper a vertical list with proper tap-target spacing
      [slot='area-navbar-mobile'] {
        display: flex;
        flex-direction: column;
        gap: 0;

        me-button,
        me-dropdown {
          display: block;
          width: 100%;

          .me-button {
            width: 100%;
            justify-content: flex-start;
            padding: var(--spacing-sm) 0; /* ✓ 12px vertical padding per link */
            font-size: var(--font-size-md);
          }
        }

        // Dropdown trigger needs space-between so chevron stays on the right
        me-dropdown .me-button.me-dropdown__trigger {
          justify-content: space-between; /* ✓ Figma: chevron on far right */

          &.is-active {
            margin-bottom: var(--spacing-md);
          }
        }
      }
    }
  }

  &__column {
    display: flex;
  }

  &__nav-column {
    display: flex;
    gap: var(--spacing-2xl);

    // The slotted wrapper (e.g. div[slot="area-navbar"]) is the only
    // direct child — propagate flex layout so the gap applies between
    // the actual nav items inside it.
    > [slot='area-navbar'] {
      display: flex;
      align-items: center;
      gap: var(--spacing-2xl); /* ✓ Figma: 32px */
    }
  }

  &__actions-column {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);

    @include desktop {
      gap: var(--spacing-md);
    }
  }

  &--default {
    .navbar-type-1__logo-column {
      order: 1;
      margin-right: auto;
    }

    .navbar-type-1__nav-column {
      order: 2;
      flex-grow: 0;
      justify-content: flex-end;
      margin-right: 0;
    }

    .navbar-type-1__actions-column {
      display: flex;
      order: 3;
      margin-left: var(--spacing-2xl);
    }

    .navbar-type-1__mobile-toggle {
      display: none;
    }

    // Mobile styles (below 768px)
    @include below-desktop {
      .navbar-type-1__nav-column {
        display: none;
      }

      .navbar-type-1__actions-column {
        display: none;
      }

      .navbar-type-1__mobile-toggle {
        display: flex;
        order: 3;
      }
    }

    // Dynamic mobile view control (based on mobileBreakpoint prop)
    &.is-mobile-view {
      .navbar-type-1__nav-column {
        display: none;
      }

      .navbar-type-1__actions-column {
        display: none;
      }

      .navbar-type-1__mobile-toggle {
        display: flex;
        order: 3;
      }
    }
  }

  &--centered {
    .navbar-type-1__logo-column {
      order: 1;
    }

    .navbar-type-1__nav-column {
      order: 2;
      flex-grow: 1;
      justify-content: center;
    }

    .navbar-type-1__actions-column {
      display: flex;
      order: 3;
    }

    .navbar-type-1__mobile-toggle {
      display: none;
    }

    // Mobile styles (below 768px)
    @include below-desktop {
      .navbar-type-1__nav-column {
        display: none;
      }

      .navbar-type-1__actions-column {
        display: none;
      }

      .navbar-type-1__mobile-toggle {
        display: flex;
        order: 3;
      }
    }

    // Dynamic mobile view control (based on mobileBreakpoint prop)
    &.is-mobile-view {
      .navbar-type-1__nav-column {
        display: none;
      }

      .navbar-type-1__actions-column {
        display: none;
      }

      .navbar-type-1__mobile-toggle {
        display: flex;
        order: 3;
      }
    }
  }

  &--nav-first {
    .navbar-type-1__logo-column {
      order: 2;
      flex-grow: 1;
      justify-content: center;
    }

    .navbar-type-1__nav-column {
      order: 1;
    }

    .navbar-type-1__actions-column {
      display: flex;
      order: 3;
    }

    .navbar-type-1__mobile-toggle {
      display: none;
    }

    // Mobile styles (below 768px)
    @include below-desktop {
      .navbar-type-1__nav-column {
        display: none;
      }

      .navbar-type-1__actions-column {
        me-button {
          &:first-child {
            display: none;
          }
        }
      }

      .navbar-type-1__mobile-toggle {
        display: flex;
        order: 3;
      }
    }

    // Dynamic mobile view control (based on mobileBreakpoint prop)
    &.is-mobile-view {
      .navbar-type-1__nav-column {
        display: none;
      }

      .navbar-type-1__actions-column {
        me-button {
          &:first-child {
            display: none;
          }
        }
      }

      .navbar-type-1__mobile-toggle {
        display: flex;
        order: 3;
      }
    }
  }

  &--logo-nav-first {
    .navbar-type-1__logo-column {
      order: 1;
      margin-right: var(--spacing-2xl);
    }

    .navbar-type-1__nav-column {
      display: flex;
      order: 2;
      flex-grow: 0;
      justify-content: flex-start;
      margin-left: 0;
      margin-right: auto;
    }

    .navbar-type-1__actions-column {
      display: flex;
      order: 3;
    }

    .navbar-type-1__mobile-toggle {
      display: none;
    }

    // Mobile styles (below 768px)
    @include below-desktop {
      .navbar-type-1__nav-column {
        display: none;
      }

      .navbar-type-1__actions-column {
        display: none;
      }

      .navbar-type-1__mobile-toggle {
        display: flex;
        order: 3;
      }
    }

    // Dynamic mobile view control (based on mobileBreakpoint prop)
    &.is-mobile-view {
      .navbar-type-1__nav-column {
        display: none;
      }

      .navbar-type-1__actions-column {
        display: none;
      }

      .navbar-type-1__mobile-toggle {
        display: flex;
        order: 3;
      }
    }
  }

  &__mobile-toggle {
    flex-direction: column;
    justify-content: space-between;
    cursor: pointer;
    padding: var(--spacing-2xs);
    margin-left: auto;

    @include size(24px, 22px);

    position: relative;
    z-index: 102;

    &:focus {
      outline: 2px solid var(--color-brand-primary-main);
      border-radius: var(--radius-size-sm);
    }

    &__line {
      display: block;
      width: 100%;
      height: 2px;
      background-color: var(--text-color-primary);
      border-radius: var(--radius-size-sm);
      transition: all 0.3s ease;
    }

    &.is-active {
      .navbar-type-1__mobile-toggle__line {
        &:nth-child(1) {
          transform: translateY(6px) rotate(45deg);
        }

        &:nth-child(2) {
          opacity: 0;
        }

        &:nth-child(3) {
          transform: translateY(-6px) rotate(-45deg);
        }
      }
    }
  }

  &__mobile-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-md);
    min-height: 20px;
  }

  &__mobile-content {
    width: 100%;

    // Reserve space at the bottom so expanded dropdown content
    // can be fully scrolled into view on mobile.
    padding-bottom: 80px;
    padding-bottom: calc(80px + env(safe-area-inset-bottom));

    .me-dropdown {
      display: block;
      width: 100%;
    }
  }

  &__mobile-actions {
    margin-top: 0;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm); /* ✓ 12px between action buttons */

    // Propagate flex to slotted wrapper so gap applies between buttons
    > [slot='area-actions-mobile'] {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-sm); /* ✓ 12px */
    }

    .me-button {
      width: 100%;
    }
  }

  .me-button--ghost {
    font-weight: 400;
  }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

@include below-desktop {
  body:has(.navbar-type-1__container--mobile.is-open)::before {
    content: '';
    position: fixed;
    inset: 0;
    background-color: rgba(var(--color-neutral-black), 0.5);
    z-index: 90;
    transition: opacity 0.25s ease-in-out;
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    cursor: pointer;
  }
}

// Dynamic mobile view overlay
body:has(.navbar-type-1.is-mobile-view .navbar-type-1__container--mobile.is-open)::before {
  content: '';
  position: fixed;
  inset: 0;
  background-color: rgba(var(--color-neutral-black), 0.5);
  z-index: 90;
  transition: opacity 0.25s ease-in-out;
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  cursor: pointer;
}

// =============================================================================
// Host element (navbar-type-1 custom element) sticky positioning.
// =============================================================================
// Custom elements default to display: inline which doesn't support sticky.
// Block display ensures proper box model for sticky positioning.
navbar-type-1 {
  display: block;
}

// Sticky positioning on the host element. Applied via JS when the sticky prop
// is truthy. This prevents the "tight wrapper" problem: an inner <header> with
// position: sticky inside a host element of the same height has no room to
// stick, so sticky must be on the outermost wrapper.
navbar-type-1.is-sticky-host {
  position: sticky;
  top: 0;
  z-index: 100;

  // Transition for hide-on-scroll mode (host's top is animated via JS).
  transition: top 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}

